import { Layers } from "@/components/ui/icons";
import { Text, View } from "react-native";

import { CardArt } from "@/components/ui/CardArt";
import { useThemeColors } from "@/theme/theme";

/**
 * Below this width the placeholder shows a glyph instead of the product name.
 *
 * A 34pt tile fits about five characters per line, so a name there fragments mid-word rather than
 * abbreviating. At that size the tile only needs to say there is no art; the row already shows the
 * name beside it.
 */
const GLYPH_BELOW_WIDTH = 40;

/**
 * Renders a card thumbnail for inventory rows and cells.
 *
 * Shows the Product's art when present, otherwise a placeholder labelled with the product name so
 * generic or not-yet-imaged units stay identifiable. Callers size it to the trading-card aspect
 * (2.5 : 3.5) so art fills without cropping.
 */
export function CardThumb({
  uri,
  width,
  height,
  label,
  radius = 6,
}: {
  uri?: string | null;
  width: number;
  height: number;
  label?: string | null;
  radius?: number;
}) {
  const c = useThemeColors();
  const placeholder = (
    <View
      style={{ width, height, borderRadius: radius }}
      className="items-center justify-center border border-border bg-surface-muted px-1"
      // When the glyph replaces the name, a screen reader still needs the label. `accessible` is
      // required for VoiceOver, because a plain View is not an accessibility element on iOS. Inside
      // an already-labelled pressable row the ancestor's label wins, which is intended.
      accessible
      accessibilityLabel={label ? `${label}, no image` : "No image"}
    >
      {width < GLYPH_BELOW_WIDTH
        ? <Layers size={Math.round(width * 0.45)} color={c.subtle} />
        : (
          // 11px muted text rather than 9px subtle text, which falls below the AA contrast floor.
          // Tail truncation abbreviates the name instead of wrapping it mid-word.
          <Text
            className="text-center font-body text-[11px] text-muted-foreground"
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {label ?? "—"}
          </Text>
        )}
    </View>
  );
  if (!uri) return placeholder;
  return (
    <View style={{ width, height, borderRadius: radius }} className="overflow-hidden">
      <CardArt
        uri={uri}
        accessibilityLabel={label ? `${label} card art` : "Card art"}
        fallback={placeholder}
      />
    </View>
  );
}
